import { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { useAuth } from '../contexts/AuthContext';
import { tasksApi, Task } from '../services/api';
import styles from './Tasks.module.css';

const BOARDS = [
  { value: '', label: 'All Boards' },
  { value: 'charsoft', label: 'Charsoft' },
  { value: 'csl', label: 'CSL' },
  { value: 'personal', label: 'Personal' },
] as const;

const COLUMNS: { status: Task['status']; title: string }[] = [
  { status: 'todo', title: 'To Do' },
  { status: 'doing', title: 'Doing' },
  { status: 'done', title: 'Done' },
];

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function isOverdue(dateStr: string | null): boolean {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date();
}

export default function Tasks() {
  const { getIdToken } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [boardFilter, setBoardFilter] = useState<string>('');
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<Task['status'] | null>(null);

  useEffect(() => {
    loadTasks();
  }, [boardFilter]);

  async function loadTasks() {
    try {
      setLoading(true);
      setError(null);
      const token = await getIdToken();
      if (!token) throw new Error('Not authenticated');

      const filters = boardFilter ? { board: boardFilter } : undefined;
      const response = await tasksApi.list(token, filters);
      setTasks(response.tasks);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }

  async function handleMoveTask(task: Task, newStatus: Task['status']) {
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Not authenticated');

      await tasksApi.move(token, task.id, newStatus);
      loadTasks();
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err instanceof Error ? err.message : 'Failed to move task',
      });
    }
  }

  async function handleEditTask(task: Task) {
    const result = await Swal.fire({
      title: 'Edit Task',
      html: `
        <div style="text-align: left;">
          <label style="display: block; margin-bottom: 4px; font-weight: 500;">Title *</label>
          <input id="swal-title" class="swal2-input" value="${task.title}" style="width: 100%; margin: 0 0 12px 0;">

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">Description</label>
          <textarea id="swal-description" class="swal2-textarea" style="width: 100%; margin: 0 0 12px 0;">${task.description || ''}</textarea>

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">Board</label>
          <select id="swal-board" class="swal2-select" style="width: 100%; margin: 0 0 12px 0;">
            <option value="charsoft" ${task.board === 'charsoft' ? 'selected' : ''}>Charsoft</option>
            <option value="csl" ${task.board === 'csl' ? 'selected' : ''}>CSL</option>
            <option value="personal" ${task.board === 'personal' ? 'selected' : ''}>Personal</option>
          </select>

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">Priority</label>
          <select id="swal-priority" class="swal2-select" style="width: 100%; margin: 0 0 12px 0;">
            <option value="urgent" ${task.priority === 'urgent' ? 'selected' : ''}>Urgent</option>
            <option value="normal" ${task.priority === 'normal' ? 'selected' : ''}>Normal</option>
            <option value="low" ${task.priority === 'low' ? 'selected' : ''}>Low</option>
          </select>

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">Due Date</label>
          <input id="swal-due" type="date" class="swal2-input" value="${task.dueDate ? task.dueDate.split('T')[0] : ''}" style="width: 100%; margin: 0 0 12px 0;">

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">Tags (comma-separated)</label>
          <input id="swal-tags" class="swal2-input" value="${task.tags.join(', ')}" style="width: 100%; margin: 0;">
        </div>
      `,
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: 'Save Changes',
      denyButtonText: 'Delete',
      confirmButtonColor: '#3b82f6',
      denyButtonColor: '#ef4444',
      preConfirm: () => {
        const title = (document.getElementById('swal-title') as HTMLInputElement).value;
        if (!title.trim()) {
          Swal.showValidationMessage('Title is required');
          return false;
        }
        const dueValue = (document.getElementById('swal-due') as HTMLInputElement).value;
        return {
          title: title.trim(),
          description: (document.getElementById('swal-description') as HTMLTextAreaElement).value.trim() || null,
          board: (document.getElementById('swal-board') as HTMLSelectElement).value as Task['board'],
          priority: (document.getElementById('swal-priority') as HTMLSelectElement).value as Task['priority'],
          dueDate: dueValue ? new Date(dueValue).toISOString() : null,
          tags: (document.getElementById('swal-tags') as HTMLInputElement).value
            .split(',')
            .map(s => s.trim())
            .filter(Boolean),
        };
      },
    });

    if (result.isDenied) {
      handleDeleteTask(task);
      return;
    }

    if (result.value) {
      try {
        const token = await getIdToken();
        if (!token) throw new Error('Not authenticated');

        await tasksApi.update(token, task.id, result.value);

        await Swal.fire({
          icon: 'success',
          title: 'Task Updated!',
          timer: 1500,
          showConfirmButton: false,
        });

        loadTasks();
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err instanceof Error ? err.message : 'Failed to update task',
        });
      }
    }
  }

  async function handleDeleteTask(task: Task) {
    const result = await Swal.fire({
      title: 'Delete Task?',
      text: `Are you sure you want to delete "${task.title}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
      confirmButtonColor: '#ef4444',
    });

    if (result.isConfirmed) {
      try {
        const token = await getIdToken();
        if (!token) throw new Error('Not authenticated');

        await tasksApi.delete(token, task.id);

        await Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          timer: 1500,
          showConfirmButton: false,
        });

        loadTasks();
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err instanceof Error ? err.message : 'Failed to delete task',
        });
      }
    }
  }

  async function handleAddTask() {
    const { value: formValues } = await Swal.fire({
      title: 'Add New Task',
      html: `
        <div style="text-align: left;">
          <label style="display: block; margin-bottom: 4px; font-weight: 500;">Title *</label>
          <input id="swal-title" class="swal2-input" placeholder="Task title" style="width: 100%; margin: 0 0 12px 0;">

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">Description</label>
          <textarea id="swal-description" class="swal2-textarea" placeholder="Optional description" style="width: 100%; margin: 0 0 12px 0;"></textarea>

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">Board</label>
          <select id="swal-board" class="swal2-select" style="width: 100%; margin: 0 0 12px 0;">
            <option value="charsoft">Charsoft</option>
            <option value="csl">CSL</option>
            <option value="personal">Personal</option>
          </select>

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">Priority</label>
          <select id="swal-priority" class="swal2-select" style="width: 100%; margin: 0 0 12px 0;">
            <option value="normal">Normal</option>
            <option value="urgent">Urgent</option>
            <option value="low">Low</option>
          </select>

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">Due Date</label>
          <input id="swal-due" type="date" class="swal2-input" style="width: 100%; margin: 0 0 12px 0;">

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">Tags (comma-separated)</label>
          <input id="swal-tags" class="swal2-input" placeholder="feature, bug, etc." style="width: 100%; margin: 0;">
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Create Task',
      confirmButtonColor: '#3b82f6',
      preConfirm: () => {
        const title = (document.getElementById('swal-title') as HTMLInputElement).value;
        if (!title.trim()) {
          Swal.showValidationMessage('Title is required');
          return false;
        }
        const dueValue = (document.getElementById('swal-due') as HTMLInputElement).value;
        return {
          title: title.trim(),
          description: (document.getElementById('swal-description') as HTMLTextAreaElement).value.trim() || null,
          board: (document.getElementById('swal-board') as HTMLSelectElement).value as Task['board'],
          status: 'todo' as Task['status'],
          priority: (document.getElementById('swal-priority') as HTMLSelectElement).value as Task['priority'],
          dueDate: dueValue ? new Date(dueValue).toISOString() : null,
          tags: (document.getElementById('swal-tags') as HTMLInputElement).value
            .split(',')
            .map(s => s.trim())
            .filter(Boolean),
        };
      },
    });

    if (formValues) {
      try {
        const token = await getIdToken();
        if (!token) throw new Error('Not authenticated');

        await tasksApi.create(token, formValues);

        await Swal.fire({
          icon: 'success',
          title: 'Task Created!',
          timer: 1500,
          showConfirmButton: false,
        });

        loadTasks();
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err instanceof Error ? err.message : 'Failed to create task',
        });
      }
    }
  }

  function getTasksByStatus(status: Task['status']): Task[] {
    return tasks.filter(t => t.status === status);
  }

  function getPriorityClass(priority: Task['priority']): string {
    switch (priority) {
      case 'urgent': return styles.priorityUrgent;
      case 'normal': return styles.priorityNormal;
      case 'low': return styles.priorityLow;
      default: return styles.priorityNormal;
    }
  }

  // Drag and drop handlers
  function handleDragStart(e: React.DragEvent, taskId: string) {
    setDraggedTaskId(taskId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', taskId);
  }

  function handleDragEnd() {
    setDraggedTaskId(null);
    setDragOverColumn(null);
  }

  function handleDragOver(e: React.DragEvent, status: Task['status']) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(status);
  }

  function handleDragLeave() {
    setDragOverColumn(null);
  }

  function handleDrop(e: React.DragEvent, newStatus: Task['status']) {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('text/plain');
    const task = tasks.find(t => t.id === taskId);

    setDraggedTaskId(null);
    setDragOverColumn(null);

    if (task && task.status !== newStatus) {
      handleMoveTask(task, newStatus);
    }
  }

  if (loading) {
    return <div className={styles.container}><p className={styles.loading}>Loading tasks...</p></div>;
  }

  if (error) {
    return (
      <div className={styles.container}>
        <p className={styles.error}>{error}</p>
        <button onClick={loadTasks} className="btn btn-primary">Retry</button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Tasks</h1>
        <div className={styles.actions}>
          <div className={styles.tabs}>
            {BOARDS.map(board => (
              <button
                key={board.value}
                className={`${styles.tab} ${boardFilter === board.value ? styles.tabActive : ''}`}
                onClick={() => setBoardFilter(board.value)}
              >
                {board.label}
              </button>
            ))}
          </div>
          <button className="btn btn-primary" onClick={handleAddTask}>+ Add Task</button>
        </div>
      </div>

      <div className={styles.board}>
        {COLUMNS.map(column => {
          const columnTasks = getTasksByStatus(column.status);
          const columnClass = column.status === 'todo' ? styles.columnTodo
            : column.status === 'doing' ? styles.columnDoing
            : styles.columnDone;

          return (
            <div
              key={column.status}
              className={`${styles.column} ${columnClass} ${dragOverColumn === column.status ? styles.columnDragOver : ''}`}
              onDragOver={(e) => handleDragOver(e, column.status)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, column.status)}
            >
              <div className={styles.columnHeader}>
                <span className={styles.columnTitle}>{column.title}</span>
                <span className={styles.columnCount}>{columnTasks.length}</span>
              </div>
              <div className={styles.columnCards}>
                {columnTasks.length === 0 ? (
                  <p className={styles.emptyColumn}>No tasks</p>
                ) : (
                  columnTasks.map(task => (
                    <div
                      key={task.id}
                      className={`${styles.card} ${draggedTaskId === task.id ? styles.cardDragging : ''}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, task.id)}
                      onDragEnd={handleDragEnd}
                      onClick={() => handleEditTask(task)}
                    >
                      <div className={styles.cardHeader}>
                        <h4 className={styles.cardTitle}>{task.title}</h4>
                        <span className={`${styles.priority} ${getPriorityClass(task.priority)}`}>
                          {task.priority}
                        </span>
                      </div>
                      {task.description && (
                        <p className={styles.cardDescription}>{task.description}</p>
                      )}
                      <div className={styles.cardMeta}>
                        {task.dueDate && (
                          <span className={`${styles.dueDate} ${isOverdue(task.dueDate) && task.status !== 'done' ? styles.dueDateOverdue : ''}`}>
                            {isOverdue(task.dueDate) && task.status !== 'done' ? 'Overdue: ' : 'Due: '}
                            {formatDate(task.dueDate)}
                          </span>
                        )}
                        {task.tags.length > 0 && (
                          <div className={styles.tags}>
                            {task.tags.slice(0, 3).map(tag => (
                              <span key={tag} className={styles.tag}>{tag}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className={styles.cardActions} onClick={e => e.stopPropagation()}>
                        <button
                          className={styles.moveBtn}
                          disabled={column.status === 'todo'}
                          onClick={() => handleMoveTask(task, column.status === 'done' ? 'doing' : 'todo')}
                        >
                          ← {column.status === 'done' ? 'Doing' : 'To Do'}
                        </button>
                        <button
                          className={styles.moveBtn}
                          disabled={column.status === 'done'}
                          onClick={() => handleMoveTask(task, column.status === 'todo' ? 'doing' : 'done')}
                        >
                          {column.status === 'todo' ? 'Doing' : 'Done'} →
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
